import { Header } from '@/components/layout/header'
import {
  PATIENT_DEFAULT_HIDDEN_COLUMNS,
  getPatientColumns,
} from '@/components/patients/patient-columns'
import { PatientSheet } from '@/components/patients/patient-sheet'
import { PatientToolbar } from '@/components/patients/patient-toolbar'
import { DataTable } from '@/components/ui/data-table'
import { usePatients, usePatientsWaSummary } from '@/hooks/usePatients'
import type { LastVisitFilter, Patient, WaFilter } from '@/lib/api-client'
import { api } from '@/lib/api-client'
import { exportPatientsToCSV } from '@/lib/export'
import { type BranchId, useBranch } from '@/providers/branch-provider'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { RowSelectionState, SortingState, VisibilityState } from '@tanstack/react-table'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'
import * as React from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/patients')({
  component: PatientsPage,
})

// Branch code to ID mapping
const BRANCH_IDS: Record<BranchId, number | undefined> = {
  all: undefined,
  'shah-alam': 1,
  putrajaya: 2,
  bangi: 3,
  'kuala-lumpur': 4,
}

function PatientsPage() {
  const { branchId } = useBranch()
  const navigate = useNavigate()

  // Table state
  const [page, setPage] = React.useState(1)
  const [pageSize] = React.useState(20)
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({})
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(
    PATIENT_DEFAULT_HIDDEN_COLUMNS
  )

  // Search and filters
  const [search, setSearch] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')
  const [lastVisitFilter, setLastVisitFilter] = React.useState<LastVisitFilter>('all')
  const [waFilter, setWaFilter] = React.useState<WaFilter>('any')

  // Export state
  const [isExporting, setIsExporting] = React.useState(false)

  // Sheet state
  const [sheetOpen, setSheetOpen] = React.useState(false)
  const [selectedPatientId, setSelectedPatientId] = React.useState<number | null>(null)

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1) // Reset to first page on search
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  // Reset page when filters change
  React.useEffect(() => {
    setPage(1)
  }, [branchId, lastVisitFilter, waFilter])

  // Get sort params from state
  const sortId = sorting[0]?.id === 'waHistory' ? 'waLastSentAt' : sorting[0]?.id
  const sortBy = sortId as
    | 'name'
    | 'pno'
    | 'totalVisits'
    | 'totalSpent'
    | 'lastVisitDate'
    | 'createdAt'
    | 'waLastSentAt'
    | undefined
  const sortOrder = sorting[0]?.desc ? 'desc' : 'asc'

  // Fetch patients
  const { data, isLoading } = usePatients({
    branchId: BRANCH_IDS[branchId],
    search: debouncedSearch || undefined,
    page,
    pageSize,
    sortBy,
    sortOrder: sorting.length > 0 ? sortOrder : undefined,
    lastVisitFilter: lastVisitFilter !== 'all' ? lastVisitFilter : undefined,
    waFilter: waFilter !== 'any' ? waFilter : undefined,
  })

  // Pool sizes for the WhatsApp filter dropdown (respects branch + last-visit + search)
  const { data: waSummary } = usePatientsWaSummary({
    branchId: BRANCH_IDS[branchId],
    lastVisitFilter: lastVisitFilter !== 'all' ? lastVisitFilter : undefined,
    search: debouncedSearch || undefined,
  })

  // Handle export all filtered
  const handleExportAll = React.useCallback(async () => {
    setIsExporting(true)
    try {
      const response = await api.patients.export({
        branchId: BRANCH_IDS[branchId],
        lastVisitFilter: lastVisitFilter !== 'all' ? lastVisitFilter : undefined,
        waFilter: waFilter !== 'any' ? waFilter : undefined,
        hasPhone: true,
        format: 'full',
        search: debouncedSearch || undefined,
      })

      if (response.data.length === 0) {
        toast.error('No patients to export')
        return
      }

      const result = exportPatientsToCSV(response.data, { format: 'full' })
      if (result.success) {
        toast.success(`Exported ${result.count} patients to ${result.filename}`)
      } else {
        toast.error('Export failed - no valid phone numbers')
      }
    } catch (error) {
      toast.error('Failed to export patients')
      console.error('Export error:', error)
    } finally {
      setIsExporting(false)
    }
  }, [branchId, lastVisitFilter, waFilter, debouncedSearch])

  // Handlers
  const handleView = React.useCallback((patient: Patient) => {
    setSelectedPatientId(patient.id)
    setSheetOpen(true)
  }, [])

  const handleEdit = React.useCallback(
    (patient: Patient) => {
      navigate({ to: '/patients/$id', params: { id: String(patient.id) } })
    },
    [navigate]
  )

  const handleRowClick = React.useCallback(
    (patient: Patient) => {
      handleView(patient)
    },
    [handleView]
  )

  // Memoize columns to prevent re-renders
  const columns = React.useMemo(
    () => getPatientColumns({ onView: handleView, onEdit: handleEdit }),
    [handleView, handleEdit]
  )

  // Create table instance
  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    pageCount: data?.pageCount ?? -1,
    state: {
      sorting,
      rowSelection,
      columnVisibility,
      pagination: {
        pageIndex: page - 1,
        pageSize,
      },
    },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === 'function' ? updater({ pageIndex: page - 1, pageSize }) : updater
      setPage(newPagination.pageIndex + 1)
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  })

  return (
    <div className="flex flex-col h-full">
      <Header
        title="Patients"
        subtitle={data ? `${data.total.toLocaleString()} patients` : 'Loading...'}
      />

      <div className="flex-1 p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4">
        {/* Toolbar */}
        <PatientToolbar
          search={search}
          onSearchChange={setSearch}
          lastVisitFilter={lastVisitFilter}
          onLastVisitFilterChange={setLastVisitFilter}
          waFilter={waFilter}
          onWaFilterChange={setWaFilter}
          waSummary={waSummary}
          table={table}
          totalFiltered={data?.total}
          onExportAll={handleExportAll}
          isExporting={isExporting}
        />

        {/* Table */}
        <DataTable
          columns={columns}
          data={data?.data ?? []}
          isLoading={isLoading}
          pageCount={data?.pageCount}
          pageIndex={page - 1}
          pageSize={pageSize}
          onPaginationChange={({ pageIndex }) => setPage(pageIndex + 1)}
          sorting={sorting}
          onSortingChange={setSorting}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          onRowClick={handleRowClick}
          emptyMessage="No patients found. Try adjusting your search or filters."
        />
      </div>

      {/* Patient Detail Sheet */}
      <PatientSheet patientId={selectedPatientId} open={sheetOpen} onOpenChange={setSheetOpen} />
    </div>
  )
}
